import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import * as signalR from '@microsoft/signalr';
import { AppDispatch } from '../store';
import { updateTask, addTask, deleteTask } from '../store/slices/tasksSlice';
import { startTimer, stopTimer } from '../store/slices/timeTrackingSlice';

let connection: signalR.HubConnection | null = null;

export const useRealTimeUpdates = (isAuthenticated: boolean) => {
  const dispatch = useDispatch<AppDispatch>();
  const connectionRef = useRef<signalR.HubConnection | null>(null);

  useEffect(() => {
    if (isAuthenticated && !connectionRef.current) {
      const token = localStorage.getItem('authToken');
      const socketUrl = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${socketUrl}/hubs/task`, {
          accessTokenFactory: () => token || '',
        })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      // Listen for task updates
      newConnection.on('TaskUpdated', (projectId: string, data: any) => {
        dispatch(updateTask(data));
      });

      newConnection.on('TaskCreated', (projectId: string, data: any) => {
        dispatch(addTask(data));
      });

      newConnection.on('TaskDeleted', (projectId: string, taskId: string) => {
        dispatch(deleteTask(taskId));
      });

      // Listen for timer updates
      newConnection.on('TimerStarted', (userId: string, data: any) => {
        dispatch(startTimer(data));
      });

      newConnection.on('TimerStopped', (userId: string, data: any) => {
        dispatch(stopTimer(data));
      });

      // Listen for notifications
      newConnection.on('NotificationReceived', (notification: any) => {
        console.log('Notification received:', notification);
      });

      newConnection.onclose(() => {
        console.log('Disconnected from real-time updates');
      });

      newConnection.onreconnecting(() => {
        console.log('Reconnecting to real-time updates...');
      });

      newConnection.onreconnected(() => {
        console.log('Reconnected to real-time updates');
      });

      // Start connection
      newConnection.start()
        .then(() => {
          console.log('Connected to real-time updates');
          // Join organization group (handled server-side via JWT claims)
        })
        .catch((err: any) => {
          console.error('SignalR connection error:', err);
        });

      connectionRef.current = newConnection;
      connection = newConnection;
    }

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
        connection = null;
      }
    };
  }, [isAuthenticated, dispatch]);

  const joinProject = (projectId: string) => {
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      connection.invoke('JoinProjectGroup', projectId).catch(console.error);
    }
  };

  const leaveProject = (projectId: string) => {
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      connection.invoke('LeaveProjectGroup', projectId).catch(console.error);
    }
  };

  return { joinProject, leaveProject };
};
